import { onLoad, init, captureException } from '@sentry/browser';
import { isBrowserTooOld } from './helpers/browse-age-detection';

const MAX_REPORTS = 10;
const reportedSources = new Set();
// Sources whose re-check is still running, so duplicate error events don't start a second probe
const pendingSources = new Set();

// Images we neither host nor control: tracking pixels, vendor CDNs, and assets injected into the
// page by browser extensions. Matched as substrings against the image src.
const ignoredImageHosts = [
  '.goog',
  'klaviyo.com',
  // Klaviyo's image CDN — same origin of content as klaviyo.com, different host
  'd3k81ch9hvuctc.cloudfront.net',
  'shopifycloud',
  'bing.com',
  'roeye.com',
  // Favicon injected by the WhatsApp browser extension, never part of our markup
  'web.whatsapp.com',
  // Logo of the Pandectes cookie banner app, served from the store's myshopify domain and
  // routinely blocked by cookie-banner blocklists
  'pandectes',
];

// The market domains the storefront is served on
const marketHostPattern = /^(?:(?:www|fr|nl|de|es|couk)\.)?tameson\.(?:com|org|es|nl|de|co\.uk|fr)$/;

// Pages are also served on the myshopify domains and theme previews. Events from any other host
// come from copies of our pages (web archives, sandboxed frames) and are not ours to fix.
const isStorefrontHost = (hostname) =>
  marketHostPattern.test(hostname) || hostname.endsWith('.myshopify.com') || hostname.endsWith('.shopifypreview.com');

// How long a re-check of a failed image may take before it is reported as timed out
const IMAGE_RECHECK_TIMEOUT_MS = 10000;

class BrokenImageError extends Error {
  constructor() {
    super('Broken image failed to load');
    this.name = 'BrokenImageError';
  }
}

// Groups broken images by host, so a broken third-party or content host gets its own issue
// instead of drowning in the storefront's own images. All market domains count as one host.
function getImageHostKey(src) {
  try {
    const { hostname } = new URL(src, window.location.href);

    return marketHostPattern.test(hostname) ? 'storefront' : hostname;
  } catch {
    return 'unknown';
  }
}

// Load the image once more. The probe is not in the document, so it never re-enters onImageError.
// Resolves to 'loaded', 'failed' or 'timeout'.
function recheckImage(el, src) {
  return new Promise((resolve) => {
    const probe = new Image();
    const timeout = window.setTimeout(() => resolve('timeout'), IMAGE_RECHECK_TIMEOUT_MS);

    probe.onload = () => {
      window.clearTimeout(timeout);
      resolve('loaded');
    };

    probe.onerror = () => {
      window.clearTimeout(timeout);
      resolve('failed');
    };

    if (el.crossOrigin !== null) {
      probe.crossOrigin = el.crossOrigin;
    }

    probe.referrerPolicy = el.referrerPolicy;
    probe.src = src;
  });
}

// An image `error` event also fires when the client drops the request: a flaky connection, a
// navigation, a blocked host, or a scraper that blocks image requests (the probe fails there too).
// A HEAD request reads the real status where the host sends CORS headers (/cdn/shop,
// cdn.shopify.com, *.myshopify.com). Other hosts, a failed request and a timeout fall back to the
// image probe. Resolves to null when the image exists, else { recheck, httpStatus? }.
async function checkImage(el, src) {
  try {
    const response = await fetch(src, {
      method: 'HEAD',
      credentials: 'omit',
      cache: 'no-store',
      signal: AbortSignal.timeout(IMAGE_RECHECK_TIMEOUT_MS),
    });

    // Not `status`: in Sentry search that key filters on the issue status
    return response.ok ? null : { recheck: 'head', httpStatus: response.status };
  } catch {
    const recheck = await recheckImage(el, src);

    return recheck === 'loaded' ? null : { recheck };
  }
}

async function onImageError(event) {
  try {
    const el = event.target;

    if (!(el instanceof HTMLImageElement)) {
      return;
    }

    const src = el.currentSrc || el.src;

    if (!src || src.startsWith('data:')) {
      return;
    }

    if (ignoredImageHosts.some((host) => src.includes(host))) {
      return;
    }

    // Pending re-checks count towards the cap, so a page full of failing images starts at most
    // MAX_REPORTS probes
    if (
      reportedSources.has(src) ||
      pendingSources.has(src) ||
      reportedSources.size + pendingSources.size >= MAX_REPORTS
    ) {
      return;
    }

    // A removed image (navigation, re-render) is not on the page anymore
    if (navigator.onLine === false || !el.isConnected) {
      return;
    }

    // Only a broken result uses up the source, so an image that checks out can be reported later
    pendingSources.add(src);
    const broken = await checkImage(el, src).finally(() => pendingSources.delete(src));

    // A re-check that never finishes does not show the image loaded, so it is reported too
    if (!broken || reportedSources.has(src) || reportedSources.size >= MAX_REPORTS) {
      return;
    }

    reportedSources.add(src);

    const srcHost = getImageHostKey(src);

    captureException(new BrokenImageError(), {
      fingerprint: ['BrokenImageError', srcHost],
      tags: {
        src,
        srcHost,
        ...broken,
        alt: el.alt,
        pagePath: window.location.pathname,
      },
    });
  } catch {
    // Never let the monitor throw inside the global error listener
  }
}

function generateUrlPatterns(basePattern, variableParts = []) {
  if (variableParts.length === 0) {
    return [new RegExp(`${basePattern}\\/[^ \\n]*`)];
  }

  return variableParts.map((part) => new RegExp(`${basePattern}\\/${part}[^ \\n]*`));
}

function loadSentry({
  sentryDsn,
  environment,
  shop,
  tracesSampleRate,
  replaysSessionSampleRate,
  replaysOnErrorSampleRate,
}) {
  const basePattern = 'https?:\\/\\/(?:(?:www|fr|nl|de|es|couk)\\.)?tameson\\.(com|org|es|nl|de|co\\.uk|fr)';
  const denyUrls = generateUrlPatterns(basePattern, ['wpm@', 'cdn/wpm', 'shopifycloud', 'apps/']);
  const allowUrls = generateUrlPatterns(basePattern);

  onLoad(() => {
    init({
      dsn: sentryDsn,
      environment,
      initialScope: shop ? { tags: { shop } } : undefined,
      tracesSampleRate,
      replaysSessionSampleRate,
      replaysOnErrorSampleRate,
      allowUrls,
      denyUrls,
      ignoreErrors: [
        'Load failed',
        'Failed to fetch',
        'Fetch is aborted',
        "Failed to execute 'fetch' on 'Window'",
        'NetworkError',
        "Cannot read properties of null (reading 'hasLimitReached')",
        'The user denied permission to access the database',
        'r["@context"].toLowerCase',
        // Browser aborts module loading when the user navigates away mid-load (mostly Mobile Safari)
        'Importing a module script failed',
        // Non-Error promise rejections without stack traces (third-party scripts rejecting with plain objects/events)
        'captured as promise rejection',
        // Firefox-specific aborted request errors bubbling up from Shopify web-pixel sandbox workers
        'NS_BINDING_ABORTED',
        // Microsoft Outlook SafeLink scanner artifact, not a real browser session
        'Object Not Found Matching Id',
        // Safari extensions messaging a closed tab
        'Invalid call to runtime.sendMessage(). Tab not found.',
        // Firefox failing to load Shopify's own shop-js modules from cdn.shopify.com/shopifycloud
        /error loading dynamically imported module: https:\/\/cdn\.shopify\.com\/shopifycloud\//,
      ],

      // Filter out noisy alerts
      beforeSend: (event, hint) => {
        const error = hint.originalException;
        const ignoredMessages = ['<unknown>', 'db'];

        if (ignoredMessages.includes(error?.message)) {
          return null;
        }

        if (!isStorefrontHost(window.location.hostname)) {
          return null;
        }

        // Browser page translation (Chrome, Google Translate) rewrites text nodes the scripts own,
        // which can make their removeChild/insertBefore calls fail. Such events cannot be told
        // apart from a real theme bug, so they are kept and tagged for triage instead of dropped.
        const htmlClassList = document.documentElement.classList;

        if (htmlClassList.contains('translated-ltr') || htmlClassList.contains('translated-rtl')) {
          event.tags = { ...event.tags, page_translated: 'true' };
        }

        // Filter out events coming from URLs with .goog extension and klaviyo
        const hostsEndingToReject = ['.goog', 'klaviyo.com'];

        if (event.request && event.request.url) {
          const url = new URL(event.request.url);

          if (hostsEndingToReject.some((hostEnding) => url.hostname.endsWith(hostEnding))) {
            return null;
          }
        }

        // event.request headers are only enriched server-side by Sentry at ingest; in the browser
        // beforeSend runs before that, so the user agent must come from the navigator directly.
        const userAgent = window.navigator?.userAgent || event.request?.headers?.['User-Agent'];
        const exceptions = event.exception?.values || [];
        const stackFrames = exceptions.flatMap((exceptionValue) => exceptionValue.stacktrace?.frames || []);

        // Filter out noisy events from HeadlessChrome and too old browsers
        if (userAgent) {
          if (
            userAgent.includes('HeadlessChrome') ||
            userAgent.includes('moto g power (2022)') ||
            userAgent.includes('Chrome/133.0.6943') ||
            isBrowserTooOld(userAgent)
          ) {
            return null;
          }
        }

        // `await is a reserved identifier` from an inline script: no inline theme script contains
        // `await`, so it comes from an app embed or a browser extension. The same error from one
        // of the theme's own asset files (/cdn/shop/t/) is still reported.
        if (
          exceptions.some((exceptionValue) => exceptionValue.value?.includes('await is a reserved identifier')) &&
          !stackFrames.some((frame) => frame.filename?.includes('/cdn/shop/t/'))
        ) {
          return null;
        }

        // Filter out events related to a specific file path
        if (
          stackFrames.some((frame) =>
            [
              'web-pixel-shopify-custom-pixel',
              'web-pixels@',
              '/sandbox/worker',
              '/onsite/js/',
              '/cdn/shopifycloud',
            ].some((toFilter) => frame.filename?.includes(toFilter))
          )
        ) {
          return null;
        }

        // Shopify's analytics (trekkie) script, unless the stack also runs through a theme asset
        if (
          stackFrames.some((frame) => frame.filename?.includes('/cdn/s/trekkie')) &&
          !stackFrames.some((frame) => frame.filename?.includes('/cdn/shop/t/'))
        ) {
          return null;
        }

        if (event.message?.includes('is not a valid collection slug!')) {
          event.tags = event.tags || {};
          event.tags.collection_handle = event.message.match(/\[(.+)\]/)[1];
        }

        return event;
      },
    });

    document.addEventListener('error', onImageError, true);
  });
}

if (window.SentryConfig) {
  loadSentry(window.SentryConfig);
}
